import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Heart, MapPin, Leaf } from "lucide-react";

const FILTERS = [
  { key: "all", label: "Toate" },
  { key: "birds", label: "Păsări" },
  { key: "mammals", label: "Mamifere" },
  { key: "small", label: "Animale mici" },
  { key: "aquatic", label: "Zone acvatice" },
];

export function AnimalsList() {
  const [animals, setAnimals] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = filter === "all" ? {} : { category: filter };
    api.get("/animals", { params }).then(r => setAnimals(r.data)).catch(() => {});
  }, [filter]);

  const filtered = animals.filter(a => !q || a.name.toLowerCase().includes(q.toLowerCase()) || a.common_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-turaco mb-3">Catalogul animalelor</p>
        <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight max-w-3xl">Pene, blană, povești și personalități imposibil de uitat</h1>
      </div>
      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <input data-testid="animals-search" placeholder="Caută după nume sau specie..." value={q} onChange={e => setQ(e.target.value)} className="flex-1 rounded-full border border-forest-900/20 bg-cream-50 px-5 py-3 outline-none focus:border-turaco" />
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button key={f.key} data-testid={`animals-filter-${f.key}`} onClick={() => setFilter(f.key)} className={`px-4 py-2 rounded-full text-sm font-semibold transition ${filter === f.key ? "bg-forest-900 text-cream-50" : "bg-cream-50 text-forest-900 border border-forest-900/20"}`}>{f.label}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map(a => (
          <Link key={a.id} to={`/animale/${a.slug}`} data-testid={`animal-item-${a.slug}`} className="group bg-cream-50 rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform">
            <div className="aspect-[4/3] bg-forest-900 overflow-hidden">
              {a.image_url ? <img src={a.image_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /> :
                <div className="w-full h-full flex items-center justify-center text-cream-50/40"><Leaf className="w-12 h-12" /></div>}
            </div>
            <div className="p-5">
              <div className="text-xs uppercase tracking-wider text-turaco font-bold">{a.common_name}</div>
              <div className="font-serif text-2xl text-forest-900 mt-1">{a.name}</div>
              <p className="text-sm text-muted mt-2 line-clamp-2">{a.story || a.personality}</p>
              {a.is_demo && <div className="mt-3 text-[10px] bg-tucan/10 text-tucan px-2 py-1 rounded-full inline-block font-semibold">Conținut demonstrativ</div>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function AnimalProfile() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/animals/${slug}`).then(r => setA(r.data)).catch(() => {}); }, [slug]);
  if (!a) return <div className="max-w-[1360px] mx-auto px-4 py-16">Se încarcă...</div>;

  return (
    <div>
      <div className="relative aspect-[3/2] md:aspect-[16/7] bg-forest-900 overflow-hidden">
        {a.image_url && <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900 via-forest-900/40 to-transparent" />
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 relative">
        <div className="bg-cream-50 rounded-[32px] p-8 lg:p-12">
          <Link to="/animale" className="inline-flex items-center gap-2 text-turaco font-semibold mb-4"><ArrowLeft className="w-4 h-4" /> Toate animalele</Link>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-turaco">{a.common_name} {a.scientific_name && `· ${a.scientific_name}`}</p>
          <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight mt-2">{a.name}</h1>
          {a.story && <p className="mt-6 text-lg text-forest-900 leading-relaxed">{a.story}</p>}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {a.origin && <Detail label="Origine" value={a.origin} icon={MapPin} />}
            {a.habitat && <Detail label="Habitat" value={a.habitat} icon={Leaf} />}
            {a.food && <Detail label="Hrană" value={a.food} icon={Heart} />}
            {a.lifespan && <Detail label="Longevitate" value={a.lifespan} icon={null} />}
            {a.conservation && <Detail label="Conservare" value={a.conservation} icon={null} />}
            {a.curiosities && <Detail label="Curiozități" value={a.curiosities} icon={null} />}
          </div>
          {a.interaction_rules && (
            <div className="mt-8 p-4 bg-forest-900/5 rounded-2xl border border-forest-900/10">
              <div className="text-xs uppercase tracking-wider font-bold text-forest-900 mb-2">Reguli de interacțiune</div>
              <p className="text-sm text-forest-800">{a.interaction_rules}</p>
            </div>
          )}
          <p className="mt-6 text-xs text-muted italic">Informațiile despre specie au scop educativ. Interacțiunea concretă în timpul turului este decisă de echipa parcului.</p>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-turaco font-bold flex items-center gap-2">{Icon && <Icon className="w-3 h-3" />} {label}</div>
      <div className="mt-1 text-forest-900">{value}</div>
    </div>
  );
}
